// src/utils/excelExport.js
// สร้างไฟล์ Excel สำหรับ Export สถิติประจำปี (จากหน้า /temples)
import * as XLSX from 'xlsx';
import { STAT_COLUMNS } from './excelTemplate';

// สร้าง workbook: แผ่น "สถิติประจำปี" (ข้อมูลเดิม) + "รายชื่อวัด" (อ้างอิง PK) + "คำแนะนำ"
export function buildExportWorkbook({ temples, stats }) {
  const wb = XLSX.utils.book_new();

  // กรองรายการสถิติที่วัดไม่อยู่ในระบบออก (ไม่งั้นตอน import จะ error "ไม่พบวัด id")
  const templeIdSet = new Set((temples || []).map((t) => String(t.id)));
  const validStats = (stats || []).filter((s) => templeIdSet.has(String(s.temple_id)));
  const skippedCount = (stats || []).length - validStats.length;

  // ---- แผ่น "สถิติประจำปี" (หัวตาราง 2 แถว: หมวดหมู่ + ชื่อคอลัมน์) ----
  // แถว 1 = หมวดหมู่ (merge เซลล์) | แถว 2 = ชื่อภาษาไทย [รหัสฟิลด์] | แถวถัดไป = ข้อมูล
  const shortLabel = (c) => String(c.label).replace(/\s*\(.*?\)\s*/g, '').trim();
  const groupRow = [];
  const labelRow = [];
  const merges = [];
  let curGroup = null;
  let groupStart = 0;
  STAT_COLUMNS.forEach((c, idx) => {
    if (c.group !== curGroup) {
      if (curGroup) merges.push({ s: { r: 0, c: groupStart }, e: { r: 0, c: idx - 1 } });
      curGroup = c.group || '';
      groupStart = idx;
      groupRow.push(curGroup);
    } else {
      groupRow.push('');
    }
    labelRow.push(`${shortLabel(c)} [${c.key}]`);
  });
  if (curGroup) merges.push({ s: { r: 0, c: groupStart }, e: { r: 0, c: STAT_COLUMNS.length - 1 } });

  const rows = validStats.map((s) =>
    STAT_COLUMNS.map((c) => {
      if (c.key === 'temple_name') {
        const t = temples.find((x) => Number(x.id) === Number(s.temple_id));
        return t ? t.name : '';
      }
      const v = s[c.key];
      return v === undefined || v === null ? '' : v;
    })
  );
  const statsSheet = XLSX.utils.aoa_to_sheet([groupRow, labelRow, ...rows]);
  if (merges.length) statsSheet['!merges'] = merges;
  statsSheet['!cols'] = STAT_COLUMNS.map((c) => ({ wch: c.key === 'temple_name' ? 30 : 20 }));
  statsSheet['!rows'] = STAT_COLUMNS.map((_, i) => (i < 2 ? { hpt: 24 } : undefined));
  XLSX.utils.book_append_sheet(wb, statsSheet, 'สถิติประจำปี');

  // ---- แผ่น "รายชื่อวัด" (สำหรับอ้างอิง temple_id) ----
  const templeHeaders = ['temple_id', 'temple_name', 'province', 'district', 'subdistrict', 'type'];
  const templeRows = temples.map((t) => [t.id, t.name, t.province, t.district, t.subdistrict, t.type]);
  const templeSheet = XLSX.utils.aoa_to_sheet([templeHeaders, ...templeRows]);
  templeSheet['!cols'] = templeHeaders.map(() => ({ wch: 22 }));
  XLSX.utils.book_append_sheet(wb, templeSheet, 'รายชื่อวัด');

  // ---- แผ่น "คำแนะนำ" ----
  const guideRows = [
    ['คำแนะนำการใช้งานไฟล์ Excel สถิติประจำปี (บัญชีกลางปี)'],
    [''],
    ['1. เปิดไฟล์นี้แล้วกรอก/แก้ไขข้อมูลในแผ่น "สถิติประจำปี" เท่านั้น'],
    ['2. ห้ามแก้คอลัมน์ temple_id (รหัสวัด) — ต้องมีค่าและต้องมีวัดอยู่ในระบบ'],
    ['   * หากวัดยังไม่เคยถูกเพิ่มในระบบ ให้เพิ่มรายชื่อวัดก่อนผ่านหน้า "จัดการข้อมูลวัด" เพื่อให้ได้ temple_id แล้วจึง import'],
    ['3. คอลัมน์ temple_name (ชื่อวัด) เป็นข้อมูลอ้างอิง ระบบจะไม่นำเข้าช่องนี้'],
    ['4. ปี (year) ใช้ปี ค.ศ. เช่น 2026 — ถ้ากรอก พ.ศ. เช่น 2569 ระบบจะแปลงให้อัตโนมัติ'],
    ['5. ฟิลด์ที่เป็นตัวเลขให้กรอกเป็นตัวเลข (รองรับเลขไทย เช่น ๑๒๓)'],
    ['6. ถ้ามีข้อมูล (temple_id + year) เดิมอยู่แล้ว ระบบจะอัปเดตแทนที่จะเพิ่มซ้ำ'],
    ['7. แผ่น "สถิติประจำปี" มีหัวคอลัมน์ 2 แถว: แถวที่ 1 = หมวดหมู่, แถวที่ 2 = ชื่อคอลัมน์ (ภาษาไทย [รหัสฟิลด์]) — ห้ามลบ/แก้ 2 แถวนี้'],
    ['8. ระบบนำเข้าจับคู่คอลัมน์อัตโนมัติ (ชื่อไทย/อังกฤษ + ข้ามแถวหมวดหมู่) — รองรับทั้งไฟล์แบบฟอร์มใหม่และไฟล์ Export เก่า'],
    ['9. หลังกรอกเสร็จ ใช้ปุ่ม "Import Excel" ในหน้า /temples เพื่อตรวจสอบและนำเข้า (รองรับ bulk + progress bar)'],
    [''],
    ['รายละเอียดคอลัมน์:'],
    ...STAT_COLUMNS.map((c) => [`${c.header}`, `= ${c.label}`]),
  ];
  const guideSheet = XLSX.utils.aoa_to_sheet(guideRows);
  guideSheet['!cols'] = [{ wch: 32 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(wb, guideSheet, 'คำแนะนำ');

  // คืนค่า workbook + จำนวนรายการที่ถูกตัด (วัดไม่อยู่ในระบบ)
  return { wb, skippedCount };
}

// ดาวน์โหลดไฟล์ xlsx ในเบราว์เซอร์
export function downloadXlsx(wb, filename) {
  XLSX.writeFile(wb, filename);
}
